import { isPlatformBrowser } from '@angular/common';
import { computed, DestroyRef, inject, PLATFORM_ID, Service, signal } from '@angular/core';

import { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET } from '@shared/constants/breakpoints';

/**
 * Width assumed during SSR/SSG, where no real viewport exists. Desktop-first, to match the
 * statically prerendered markup and avoid a hydration flash on the common case.
 */
const SERVER_WIDTH = BREAKPOINT_DESKTOP;

/**
 * Single source of truth for viewport-size checks. Exposes reactive, mutually exclusive
 * device-class signals derived from the shared breakpoints, so components never reach for
 * `window.matchMedia` / `window.innerWidth` directly.
 */
@Service()
export class BreakpointService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Current viewport width; kept in sync with `resize` in the browser. */
  private readonly width = signal(this.isBrowser ? window.innerWidth : SERVER_WIDTH);

  /** Viewport is narrower than the tablet breakpoint (phones). */
  readonly isMobile = computed(() => this.width() < BREAKPOINT_TABLET);

  /** Viewport sits between the tablet and desktop breakpoints. */
  readonly isTablet = computed(
    () => this.width() >= BREAKPOINT_TABLET && this.width() < BREAKPOINT_DESKTOP,
  );

  /** Viewport is at least the desktop breakpoint. */
  readonly isDesktop = computed(() => this.width() >= BREAKPOINT_DESKTOP);

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    const onResize = (): void => this.width.set(window.innerWidth);

    window.addEventListener('resize', onResize, { passive: true });

    inject(DestroyRef).onDestroy(() => window.removeEventListener('resize', onResize));
  }

  /**
   * Evaluates an arbitrary media query against the current viewport, returning `false` during
   * SSR/SSG. Prefer the device-class signals above for the shared breakpoints; use this only for
   * one-off queries such as asset-specific art direction.
   */
  matches(query: string): boolean {
    return this.isBrowser && window.matchMedia(query).matches;
  }
}
